<template>
  <div class="mobile-konva-editor" ref="editorContainer" tabindex="0"
    :class="{ 'pan-ready': panMode || spacePan, 'pan-moving': navigationActive, erasing: eraserEnabled && !spacePan && !navigationActive }"
    @mouseenter="viewHovered = true" @mouseleave="viewHovered = false"
    @wheel.prevent.stop="handleViewportWheel"
    @pointerdown.capture="handleEditorPointerDown" @pointermove.capture="handleEditorPointerMove"
    @pointerup.capture="handleEditorPointerUp" @pointercancel.capture="cancelErasePointer"
    @pointerleave="hideEraseCursor"
    @mousedown.capture="guardViewportMouseDown"
    @touchstart.capture="handleViewportTouchStart" @touchmove.capture="handleViewportTouchMove"
    @touchend.capture="handleViewportTouchEnd" @touchcancel.capture="handleViewportTouchEnd"
    @click.capture="guardViewportClick">
    <div v-if="!exportMode && viewport.zoom > 1" class="viewport-background" :style="zoomedBackgroundStyle" aria-hidden="true" />
    <v-stage
      ref="stage"
      :config="stageConfig"
      @mousedown="handleStageMouseDown"
      @touchstart="handleStageMouseDown"
      @mousemove="handleStageMouseMove"
      @touchmove="handleStageMouseMove"
      @mouseup="handleStageMouseUp"
      @touchend="handleStageMouseUp"
    >
      <v-layer ref="layer" :config="viewportLayerConfig">
        <!-- All elements and groups sorted by zIndex -->
        <template v-for="canvasItem in sortedKonvaItems" :key="canvasItem.key">
          <v-shape
            v-if="hasRasterEffect(canvasItem)"
            :config="getPerspectiveConfig(canvasItem)"
            @transformend="handleWarpTransformEnd"
            @dragend="canvasItem.type === 'group' ? handleGroupDragEnd($event) : (canvasItem.type === 'text' ? handleTextDragEnd($event) : handleDragEnd($event))"
            @tap="handleWarpTap($event, canvasItem)"
            @click="handleWarpTap($event, canvasItem)"
            @dblclick="canvasItem.type === 'text' && handleTextDblClick($event)"
            @dbltap="canvasItem.type === 'text' && handleTextDblClick($event)"
          />
          <v-image
            v-else-if="canvasItem.type === 'image'"
            :config="getImageConfig(canvasItem.node)"
            @transformend="handleTransformEnd"
            @dragend="handleDragEnd"
            @tap="handleImageTap"
            @click="handleImageTap"
          />
          <v-text
            v-else-if="canvasItem.type === 'text'"
            :config="canvasItem.node.config"
            @transformend="handleTextTransformEnd"
            @dragend="handleTextDragEnd"
            @tap="handleTextTap"
            @click="handleTextTap"
            @dblclick="handleTextDblClick"
            @dbltap="handleTextDblClick"
          />
          <v-group
            v-else-if="canvasItem.type === 'group'"
            :config="canvasItem.node.config"
            @transformend="handleGroupTransformEnd"
            @dragend="handleGroupDragEnd"
            @tap="handleGroupTap"
            @click="handleGroupTap"
          >
            <template v-for="item in canvasItem.node.imageChildren" :key="'child-' + item.id">
              <v-shape v-if="isPerspectiveChild(item)"
                :config="getGroupedPerspectiveConfig(item, 'image')" />
              <v-image v-else :config="item.config" />
            </template>
            <template v-for="item in canvasItem.node.textChildren" :key="'child-text-' + item.id">
              <v-shape v-if="isPerspectiveChild(item)"
                :config="getGroupedPerspectiveConfig(item, 'text')" />
              <v-text v-else :config="item.config" />
            </template>
          </v-group>
        </template>
        
        <!-- Lasso selection line -->
        <v-line
          v-if="!exportMode && isDrawingLasso"
          :config="lassoLineConfig"
        />
        
        <!-- Selection highlight rectangles -->
        <v-rect
          v-for="id in exportMode ? [] : selectedItems"
          :key="'sel-' + id"
          :config="getSelectionRectConfig(id)"
        />
        
        <!-- Calculator highlight rectangle -->
        <v-rect
          v-if="!exportMode && highlightedItemId && highlightRectConfig"
          :config="highlightRectConfig"
        />
        
        <v-transformer ref="transformer" :config="transformerConfig" />
        <template v-if="!exportMode && !eraserEnabled && transformMode === 'perspective' && selectedTransformItem && !selectedTransformItem.locked">
          <v-line :config="perspectiveOutlineConfig" />
          <v-circle v-for="(point, index) in perspectiveHandlePoints" :key="'perspective-corner-' + index"
            :config="{ x: point.x, y: point.y, radius: 9 / viewportZoom, fill: '#fff', stroke: '#2764df', strokeWidth: 2.5 / viewportZoom, draggable: true, name: 'perspective-handle', hitStrokeWidth: 24 / viewportZoom }"
            @mousedown="stopPerspectiveEvent" @touchstart="stopPerspectiveEvent"
            @dragmove="movePerspectiveCorner($event, index)" @dragend="finishPerspectiveDrag"
          />
          <v-circle v-for="(point, index) in perspectiveEdgePoints" :key="'perspective-edge-' + index"
            :config="{ x: point.x, y: point.y, radius: 7 / viewportZoom, fill: '#2764df', stroke: '#fff', strokeWidth: 2 / viewportZoom, draggable: true, name: 'perspective-handle', hitStrokeWidth: 24 / viewportZoom }"
            @mousedown="stopPerspectiveEvent" @touchstart="stopPerspectiveEvent"
            @dragmove="movePerspectiveEdge($event, index)" @dragend="finishPerspectiveDrag"
          />
        </template>
      </v-layer>
    </v-stage>
    <div v-if="!exportMode && eraserEnabled && eraseCursor && !navigationActive"
      class="eraser-cursor" :style="eraseCursorStyle" aria-hidden="true" />
    
    
    <!-- Text edit modal -->
    <div v-if="showTextEditModal" class="text-edit-modal-overlay" @click.self="cancelTextEdit">
      <div class="text-edit-modal">
        <div class="text-edit-header">
          <span>{{ $t('common.edit') }} {{ $t('legacy.Текст') }}</span>
          <button class="text-edit-close" @click="cancelTextEdit">&times;</button>
        </div>
        <div class="text-edit-body">
          <textarea 
            ref="textEditInput"
            v-model="editingText"
            class="text-edit-textarea"
            :placeholder="$t('Введите текст...')"
            @keydown.enter.ctrl="saveTextEdit"
            @keydown.esc="cancelTextEdit"
          ></textarea>
        </div>
        <div class="text-edit-footer">
          <button class="text-edit-btn cancel" @click="cancelTextEdit">{{ $t('common.cancel') }}</button>
          <button class="text-edit-btn save" @click="saveTextEdit">{{ $t('common.save') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Konva from 'konva';
import { defaultCorners, validCorners, drawPerspective, perspectiveRasterQuality, boundedRasterRatio } from '@/utils/perspective';
import canvasViewportMixin from '@/lib/canvasViewportMixin';
import objectEraserMixin from '@/lib/objectEraserMixin';
import { applyEraser } from '@/utils/objectEraser';
import { screenToCanvas } from '@/utils/canvasViewport';

Konva.Filters.Temperature = function(imageData) {
  const temp = this.getAttr('temperature') || 0;
  if (temp === 0) return;
  const intensity = temp / 100;
  const data = imageData.data;
  const rShift = Math.round(intensity * 30);
  const bShift = Math.round(-intensity * 30);
  for (let i = 0; i < data.length; i += 4) {
    data[i]     = Math.min(255, Math.max(0, data[i]     + rShift));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + bShift));
  }
};

Konva.Filters.Shadows = function(imageData) {
  const shadows = this.getAttr('shadowsAdjust') || 0;
  if (shadows === 0) return;
  const intensity = shadows / 100;
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const luminance = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) / 255;
    const shadowFactor = Math.pow(1 - luminance, 2);
    const adjustment = Math.round(intensity * shadowFactor * 80);
    data[i]     = Math.min(255, Math.max(0, data[i]     + adjustment));
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + adjustment));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + adjustment));
  }
};

export default {
  name: 'MobileKonvaEditor',
  mixins: [canvasViewportMixin, objectEraserMixin],
  props: {
    transformMode: { type: String, default: 'resize' },
    lockAspect: { type: Boolean, default: true },
    elements: {
      type: Array,
      required: true
    },
    groups: {
      type: Array,
      default: () => []
    },
    canvasWidth: {
      type: Number,
      default: 400
    },
    canvasHeight: {
      type: Number,
      default: 300
    },
    aspectRatio: {
      type: Number,
      default: 4/3
    },
    selectionMode: {
      type: Boolean,
      default: false
    },
    excludedItems: {
      type: Array,
      default: () => []
    },
    highlightedItemId: {
      type: [Number, String, null],
      default: null
    }
  },
  emits: ['update:elements', 'update:groups', 'element-selected', 'element-deleted', 'group-selected', 'group-deleted', 'create-group', 'selection-complete', 'save-history', 'viewport-change', 'exit-pan', 'exit-eraser'],
  data() {
    return {
      stageWidth: 400,
      stageHeight: 300,
      selectedId: null,
      selectedGroupId: null,
      loadedImages: {},
      groupImageCache: {},
      imageNodes: [],
      textNodes: [],
      groupNodes: [],
      selectedItems: [],
      isDrawingLasso: false,
      lassoPoints: [],
      showTextEditModal: false,
      editingTextId: null,
      editingText: '',
      correctedGroupIds: new Set(),
      exportMode: false,
      draftPerspective: null,
      warpSources: new Map()
    };
  },
  computed: {
    stageConfig() {
      return {
        width: this.stageWidth,
        height: this.stageHeight
      };
    },
    transformerConfig() {
      return {
        visible: !this.exportMode && !this.eraserEnabled,
        rotateEnabled: true,
        keepRatio: this.lockAspect,
        enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'top-center', 'bottom-center', 'middle-left', 'middle-right'],
        boundBoxFunc: (oldBox, newBox) => {
          if (newBox.width < 20 * this.viewportZoom || newBox.height < 20 * this.viewportZoom) {
            return oldBox;
          }
          return newBox;
        }
      };
    },
    selectedTransformItem() {
      if (this.selectedGroupId) return this.groups.find(g => String(g.id) === String(this.selectedGroupId)) || null;
      if (this.selectedId) return this.elements.find(el => String(el.id) === String(this.selectedId)) || null;
      return null;
    },
    perspectiveHandlePoints() {
      const node = this.getSelectedKonvaNode();
      if (!node || !this.selectedTransformItem) return [];
      const corners = this.currentCorners(this.selectedTransformItem);
      return corners.map(point => this.localPerspectivePoint(node, point));
    },
    perspectiveEdgePoints() {
      const points = this.perspectiveHandlePoints;
      if (points.length !== 4) return [];
      return points.map((p, i) => ({
        x: (p.x + points[(i + 1) % 4].x) / 2,
        y: (p.y + points[(i + 1) % 4].y) / 2
      }));
    },
    perspectiveOutlineConfig() {
      const points = this.perspectiveHandlePoints;
      return { points: points.flatMap(p => [p.x, p.y]), closed: true,
        stroke: '#2764df', strokeWidth: 1.5 / this.viewportZoom,
        dash: [5 / this.viewportZoom, 4 / this.viewportZoom], listening: false };
    },
    ungroupedImageNodes() {
      return this.imageNodes.filter(node => !node.originalItem.groupId);
    },
    sortedKonvaItems() {
      const items = [];
      for (const node of this.ungroupedImageNodes) {
        items.push({ type: 'image', key: 'img-' + node.id, zIndex: node.originalItem.zIndex || 0, node });
      }
      for (const node of this.textNodes) {
        items.push({ type: 'text', key: 'text-' + node.id, zIndex: (node.originalItem && node.originalItem.zIndex) || 0, node });
      }
      for (const node of this.groupNodes) {
        items.push({ type: 'group', key: 'group-' + node.id, zIndex: node.originalGroup.zIndex || 0, node });
      }
      return items.sort((a, b) => a.zIndex - b.zIndex);
    },
    lassoLineConfig() {
      return {
        points: this.lassoPoints,
        stroke: '#007bff',
        strokeWidth: 2 / this.viewportZoom,
        dash: [5 / this.viewportZoom, 5 / this.viewportZoom],
        closed: false,
        listening: false
      };
    },
    highlightRectConfig() {
      if (!this.highlightedItemId) return null;
      
      // Ищем элемент в imageNodes или textNodes
      let node = this.imageNodes.find(n => n.originalItem && n.originalItem.id === this.highlightedItemId);
      if (!node) {
        node = this.textNodes.find(n => n.originalItem && n.originalItem.id === this.highlightedItemId);
      }
      
      if (!node || !node.config) return null;
      
      const config = node.config;
      const padding = 6;
      
      return {
        x: config.x - padding,
        y: config.y - padding,
        width: (config.width || 100) + padding * 2,
        height: (config.height || 100) + padding * 2,
        offsetX: config.offsetX || 0,
        offsetY: config.offsetY || 0,
        rotation: config.rotation || 0,
        stroke: '#ff6b00',
        strokeWidth: 3,
        dash: [8, 4],
        cornerRadius: 4,
        listening: false
      };
    }
  },
  watch: {
    transformMode() {
      this.draftPerspective = null;
      this.$nextTick(() => this.updateTransformer());
    },
    elements: {
      handler() {
        if (this.eraseDraft && !this.eraserCommitting) this.cancelEraseStroke();
        this.loadImages();
        this.loadTexts();
      },
      deep: true,
      immediate: true
    },
    groups: {
      handler() {
        if (this.eraseDraft && !this.eraserCommitting) this.cancelEraseStroke();
        this.loadGroups();
      },
      deep: true,
      immediate: true
    },
    selectionMode(newVal) {
      if (!newVal) {
        this.isDrawingLasso = false;
        this.lassoPoints = [];
      }
    },
    excludedItems: {
      handler() {
        this.loadImages();
        this.loadTexts();
      },
      deep: true
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleWindowResize);

    // Use ResizeObserver to catch when the container layout actually settles.
    // We observe the PARENT element (CSS-controlled) not editorContainer itself,
    // because editorContainer's height is driven by Konva content and may not
    // reflect the true CSS-computed size we want.
    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newWidth = Math.round(entry.contentRect.width);
          if (newWidth > 0 && newWidth !== this.stageWidth) {
            this.stageWidth = newWidth;
            this.stageHeight = Math.round(newWidth / this.aspectRatio);
            // Re-render all elements with corrected scale
            this.$nextTick(() => {
              this.loadImages();
              this.loadTexts();
              this.loadGroups();
            });
          }
        }
      });
      // Observe parent (.content-section) — it's sized purely by CSS (aspect-ratio + width: 100%)
      // so its clientWidth is always the true container width regardless of Konva stage size.
      const observeTarget = this.$refs.editorContainer.parentElement || this.$refs.editorContainer;
      this._resizeObserver.observe(observeTarget);
    }

    // Initial calculation: defer two frames so CSS aspect-ratio has time to resolve.
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.calculateStageSize();
          this.loadImages();
          this.loadTexts();
          this.loadGroups();
        });
      });
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  },
  methods: {
    isPerspectiveChild(child) {
      return validCorners(child.originalItem?.perspective) || this.itemHasErasure(child.originalItem);
    },
    getGroupedPerspectiveConfig(child, type, minimumDensity = 1) {
      return { ...this.getPerspectiveConfig({
        type, key: 'group-child-' + child.id, node: child
      }, minimumDensity), draggable: false };
    },
    async setExportMode(enabled) {
      if (enabled) this.cancelEraseStroke();
      this.exportMode = enabled;
      await this.$nextTick();
      this.$refs.transformer?.getNode()?.forceUpdate();
      this.$refs.layer?.getNode()?.draw();
    },
    currentCorners(item) {
      if (this.draftPerspective && String(this.draftPerspective.id) === String(item.id)
        && this.draftPerspective.group === Boolean(this.selectedGroupId)) {
        return this.draftPerspective.corners;
      }
      return validCorners(item.perspective) ? item.perspective : defaultCorners();
    },
    hasRasterEffect(canvasItem) {
      return validCorners(canvasItem.node.originalItem?.perspective || canvasItem.node.originalGroup?.perspective)
        || this.itemHasErasure(canvasItem.node.originalItem || canvasItem.node.originalGroup)
        || this.isEraseDraft(canvasItem)
        || Boolean(this.draftPerspective
          && String(this.draftPerspective.id) === String(canvasItem.node.id)
          && this.draftPerspective.group === (canvasItem.type === 'group'));
    },
    getWarpSource(canvasItem, minimumDensity = 1, unmasked = false) {
      if (!unmasked && this.isEraseDraft(canvasItem)) return this.eraseDraft.source;
      const model = canvasItem.node.originalItem || canvasItem.node.originalGroup;
      const config = canvasItem.node.config;
      const density = Math.max(minimumDensity, this.exportMode ? 3
        : (this.viewportZoom || 1) * (Konva.pixelRatio || 1));
      const corners = this.currentCorners ? this.currentCorners(model) : model?.perspective;
      const quality = perspectiveRasterQuality(config.width, config.height, corners,
        density * Math.max(Math.abs(config.scaleX || 1), Math.abs(config.scaleY || 1)));
      const cached = this.warpSources.get(canvasItem.key);
      if (!unmasked && cached?.node === canvasItem.node && cached.quality === quality) return cached.source;
      const data = canvasItem.node;
      const { width, height } = data.config;
      if (!width || !height) return null;
      // Render the unmodified source to an offscreen canvas, never rasterize
      // the already warped result: subsequent edits remain reversible.
      const source = new Konva.Group({ x: width / 2, y: height / 2 });
      const filteredNodes = [];
      const children = canvasItem.type === 'group'
        ? [...data.imageChildren.map(child => ({ ...child, type: 'image' })),
          ...data.textChildren.map(child => ({ ...child, type: 'text' }))]
        : [{ ...data, type: canvasItem.type }];
      let minX = 0, minY = 0, maxX = width, maxY = height;
      for (const child of children) {
        let node;
        if (canvasItem.type === 'group' && this.isPerspectiveChild(child)) {
          node = new Konva.Shape(this.getGroupedPerspectiveConfig(child, child.type, quality));
        } else {
          const config = canvasItem.type === 'group'
            ? { ...child.config, draggable: false }
            : { ...child.config, x: 0, y: 0, rotation: 0,
              scaleX: 1, scaleY: 1,
              offsetX: width / 2, offsetY: height / 2, draggable: false, opacity: 1 };
          node = child.type === 'text' ? new Konva.Text(config) : new Konva.Image(config);
          if (child.needsCache) filteredNodes.push({ node, config });
        }
        source.add(node);
        if (node.hasShadow?.()) {
          const bounds = node.getClientRect();
          minX = Math.min(minX, bounds.x); minY = Math.min(minY, bounds.y);
          maxX = Math.max(maxX, bounds.x + bounds.width);
          maxY = Math.max(maxY, bounds.y + bounds.height);
        }
        if (canvasItem.type === 'group') {
          const corners = validCorners(child.originalItem?.perspective)
            ? child.originalItem.perspective : defaultCorners();
          for (const point of corners) {
            const projected = node.getAbsoluteTransform().point({
              x: point.x * child.config.width, y: point.y * child.config.height
            });
            minX = Math.min(minX, projected.x); minY = Math.min(minY, projected.y);
            maxX = Math.max(maxX, projected.x); maxY = Math.max(maxY, projected.y);
          }
        }
      }
      // Include child pixels beyond the original group rectangle. Their source
      // coordinates are then projected through the same group homography.
      const rasterWidth = Math.ceil(maxX - minX), rasterHeight = Math.ceil(maxY - minY);
      const pixelRatio = boundedRasterRatio(rasterWidth, rasterHeight, quality);
      for (const { node, config } of filteredNodes) {
        node.cache({ pixelRatio: boundedRasterRatio(config.width, config.height,
          pixelRatio * Math.max(Math.abs(config.scaleX || 1), Math.abs(config.scaleY || 1))) });
      }
      const canvas = source.toCanvas({ x: minX, y: minY,
        width: rasterWidth, height: rasterHeight, pixelRatio });
      source.destroy();
      const result = { canvas, pixelRatio, sourceRect: {
        x: -minX * pixelRatio, y: -minY * pixelRatio, width: width * pixelRatio, height: height * pixelRatio
      } };
      const item = data.originalItem || data.originalGroup;
      if (!unmasked) {
        applyEraser(canvas.getContext('2d'), this.displayEraser(item, canvasItem.type), result.sourceRect);
        this.warpSources.set(canvasItem.key, { node: data, quality, source: result });
      }
      return result;
    },
    getPerspectiveConfig(canvasItem, minimumDensity = 1) {
      const data = canvasItem.node;
      const item = data.originalItem || data.originalGroup;
      const config = data.config;
      const corners = this.currentCorners(item);
      const source = this.getWarpSource(canvasItem, minimumDensity);
      return {
        id: String(data.id), name: canvasItem.type === 'group' ? 'group' : 'element',
        x: config.x, y: config.y, width: config.width, height: config.height,
        offsetX: config.width / 2, offsetY: config.height / 2,
        rotation: config.rotation || 0, scaleX: config.scaleX || 1,
        scaleY: config.scaleY || 1, opacity: config.opacity,
        draggable: !item.locked && !this.selectionMode && !this.eraserEnabled,
        listening: !this.isEraseDraft(canvasItem),
        fill: '#000',
        sceneFunc: context => {
          if (!source) return;
          if (!validCorners(item.perspective) && !this.draftPerspective) {
            const ratio = source.pixelRatio || 1;
            context._context.drawImage(source.canvas, -source.sourceRect.x / ratio, -source.sourceRect.y / ratio,
              source.canvas.width / ratio, source.canvas.height / ratio);
          } else drawPerspective(context._context, source.canvas,
            config.width, config.height, corners, source.sourceRect,
            this.isEraseDraft(canvasItem) ? this.eraseDraft.paths.length : 0);
        },
        hitFunc: (context, shape) => {
          if (!source) return;
          // Preserve source transparency in Konva's hit canvas, so a warped
          // group cannot steal clicks in the empty gaps between its children.
          if (!source.hitMasks) source.hitMasks = new Map();
          let mask = source.hitMasks.get(shape.colorKey);
          if (!mask) {
            mask = document.createElement('canvas');
            mask.width = source.canvas.width;
            mask.height = source.canvas.height;
            const maskContext = mask.getContext('2d');
            maskContext.drawImage(source.canvas, 0, 0);
            const pixels = maskContext.getImageData(0, 0, mask.width, mask.height);
            const color = parseInt(shape.colorKey.slice(1), 16);
            for (let i = 0; i < pixels.data.length; i += 4) {
              if (pixels.data[i + 3] > 8) {
                pixels.data[i] = (color >> 16) & 255;
                pixels.data[i + 1] = (color >> 8) & 255;
                pixels.data[i + 2] = color & 255;
                pixels.data[i + 3] = 255;
              } else {
                pixels.data[i + 3] = 0;
              }
            }
            maskContext.putImageData(pixels, 0, 0);
            source.hitMasks.set(shape.colorKey, mask);
          }
          if (!validCorners(item.perspective) && !this.draftPerspective) {
            const ratio = source.pixelRatio || 1;
            context._context.drawImage(mask, -source.sourceRect.x / ratio, -source.sourceRect.y / ratio,
              mask.width / ratio, mask.height / ratio);
          } else drawPerspective(context._context, mask, config.width, config.height,
            corners, source.sourceRect, 0);
        }
      };
    },
    handleWarpTap(e, item) {
      if (item.type === 'group') this.handleGroupTap(e);
      else if (item.type === 'text') this.handleTextTap(e);
      else this.handleImageTap(e);
    },
    handleWarpTransformEnd(e) {
      const id = String(e.target.id());
      if (this.selectedGroupId && String(this.selectedGroupId) === id) this.handleGroupTransformEnd(e);
      else if (this.elements.find(item => String(item.id) === id)?.type === 'text') this.handleTextTransformEnd(e);
      else this.handleTransformEnd(e);
    },
    getSelectedKonvaNode() {
      const stage = this.$refs.stage?.getNode();
      const id = this.selectedGroupId || this.selectedId;
      return stage && id != null ? stage.findOne('#' + String(id)) : null;
    },
    localPerspectivePoint(node, point) {
      const width = node.width(), height = node.height();
      const isOriginalGroup = node.getClassName() === 'Group';
      return node.getTransform().point({
        x: point.x * width - (isOriginalGroup ? width / 2 : 0),
        y: point.y * height - (isOriginalGroup ? height / 2 : 0)
      });
    },
    stopPerspectiveEvent(e) {
      e.cancelBubble = true;
    },
    perspectivePointer() {
      const node = this.getSelectedKonvaNode();
      const stage = this.$refs.stage?.getNode();
      const pointer = stage?.getPointerPosition();
      if (!node || !pointer || !node.width() || !node.height()) return null;
      const local = node.getAbsoluteTransform().copy().invert().point(pointer);
      const isOriginalGroup = node.getClassName() === 'Group';
      return {
        x: Math.max(-1, Math.min(2, (local.x + (isOriginalGroup ? node.width() / 2 : 0)) / node.width())),
        y: Math.max(-1, Math.min(2, (local.y + (isOriginalGroup ? node.height() / 2 : 0)) / node.height()))
      };
    },
    movePerspectiveCorner(e, index) {
      if (this.shouldBlockInteraction()) return;
      const point = this.perspectivePointer();
      if (!point || !this.selectedTransformItem) return;
      const next = this.currentCorners(this.selectedTransformItem).map(p => ({ ...p }));
      next[index] = point;
      if (validCorners(next)) this.draftPerspective = {
        id: this.selectedTransformItem.id, group: Boolean(this.selectedGroupId), corners: next
      };
      else e.target.position(this.perspectiveHandlePoints[index]);
    },
    movePerspectiveEdge(e, index) {
      if (this.shouldBlockInteraction()) return;
      const point = this.perspectivePointer();
      if (!point || !this.selectedTransformItem) return;
      const next = this.currentCorners(this.selectedTransformItem).map(p => ({ ...p }));
      const other = (index + 1) % 4;
      const midpoint = { x: (next[index].x + next[other].x) / 2,
        y: (next[index].y + next[other].y) / 2 };
      const offset = { x: point.x - midpoint.x, y: point.y - midpoint.y };
      next[index].x += offset.x; next[index].y += offset.y;
      next[other].x += offset.x; next[other].y += offset.y;
      if (validCorners(next)) this.draftPerspective = {
        id: this.selectedTransformItem.id, group: Boolean(this.selectedGroupId), corners: next
      };
      else e.target.position(this.perspectiveEdgePoints[index]);
    },
    finishPerspectiveDrag() {
      if (this.shouldBlockInteraction()) return;
      if (!this.draftPerspective) return;
      const { id, group, corners } = this.draftPerspective;
      this.$emit(group ? 'update:groups' : 'update:elements',
        (group ? this.groups : this.elements).map(item =>
          String(item.id) === String(id) ? { ...item, perspective: corners.map(p => ({ ...p })) } : item
        ));
      this.$nextTick(() => { this.draftPerspective = null; });
    },
    resetPerspective() {
      const item = this.selectedTransformItem;
      if (!item) return;
      const group = Boolean(this.selectedGroupId);
      this.draftPerspective = null;
      this.$emit(group ? 'update:groups' : 'update:elements',
        (group ? this.groups : this.elements).map(entry => {
          if (String(entry.id) !== String(item.id)) return entry;
          const updated = { ...entry };
          delete updated.perspective;
          return updated;
        }));
    },
    handleWindowResize() {
      // Window resize is handled by ResizeObserver on the container.
      // This listener is kept as a fallback for browsers without ResizeObserver support.
      this.calculateStageSize();
    },
    clearSelection() {
      this.selectedItems = [];
      this.selectedGroupId = null;
      this.updateTransformer();
    },
    
    calculateStageSize() {
      const container = this.$refs.editorContainer;
      if (!container) return;
      
      // getBoundingClientRect on the PARENT gives the true CSS-rendered width
      // regardless of flex/padding/box-sizing complexity.
      // The parent (.content-section) is sized by CSS; editorContainer itself
      // is sized by Konva content, so reading it would create a circular dependency.
      const parent = container.parentElement || container;
      const rect = parent.getBoundingClientRect();
      const width = Math.round(rect.width);
      
      if (width > 0) {
        this.stageWidth = width;
        this.stageHeight = Math.round(width / this.aspectRatio);
      }
    },
    
    async loadImages() {
      const nodes = [];
      const scale = this.stageWidth / this.canvasWidth;
      
      for (const item of this.elements) {
        if (item.isBackground || item.isText) continue;
        if (!item.src) continue;
        if (this.excludedItems.includes(item.id)) continue;
        if (item.groupId) continue;
        
        try {
          let baseImage = this.loadedImages[item.src];
          if (!baseImage) {
            baseImage = await this.loadImage(item.src);
            this.loadedImages[item.src] = baseImage;
          }
          
          const scaledWidth = (item.width || 100) * scale;
          const scaledHeight = (item.height || 100) * scale;
          const centerX = scaledWidth / 2;
          const centerY = scaledHeight / 2;
          
          const hasFilters = item.filters && Object.keys(item.filters).some(k => item.filters[k] !== 0);
          const konvaFilters = hasFilters ? this.getKonvaFilters(item.filters) : null;
          
          const imageSource = item.flipX
            ? this.createFlippedCanvas(baseImage)
            : baseImage;

          const config = {
            id: String(item.id),
            name: 'element',
            x: ((item.x || 0) * scale) + centerX,
            y: ((item.y || 0) * scale) + centerY,
            width: scaledWidth,
            height: scaledHeight,
            rotation: item.rotate || 0,
            scaleX: 1,
            scaleY: 1,
            opacity: item.opacity !== undefined ? item.opacity : 1,
            image: imageSource,
            draggable: !item.groupId && !item.locked,
            offsetX: centerX,
            offsetY: centerY
          };
          
          if (hasFilters && konvaFilters) {
            config.filters = konvaFilters.filters;
            if (konvaFilters.brightness !== undefined) config.brightness = konvaFilters.brightness;
            if (konvaFilters.contrast !== undefined) config.contrast = konvaFilters.contrast;
            if (konvaFilters.saturation !== undefined) config.saturation = konvaFilters.saturation;
            if (konvaFilters.hue !== undefined) config.hue = konvaFilters.hue;
            if (konvaFilters.temperature !== undefined) config.temperature = konvaFilters.temperature;
            if (konvaFilters.shadowsAdjust !== undefined) config.shadowsAdjust = konvaFilters.shadowsAdjust;
          }
          
          nodes.push({
            id: item.id,
            originalItem: item,
            config: config,
            needsCache: hasFilters
          });
        } catch (err) {
          console.error('Failed to load image:', item.src, err);
        }
      }
      
      this.imageNodes = nodes.sort((a, b) => 
        (a.originalItem.zIndex || 0) - (b.originalItem.zIndex || 0)
      );
      
      this.$nextTick(() => {
        this.cacheFilteredImages();
        this.updateTransformer();
        this.loadGroups();
      });
    },
    
    createFlippedCanvas(image) {
      const w = image.naturalWidth || image.width;
      const h = image.naturalHeight || image.height;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(image, 0, 0, w, h);
      return canvas;
    },

    getKonvaFilters(filters) {
      const konvaFiltersList = [];
      const result = { filters: null };
      
      if (filters.exposure !== undefined && filters.exposure !== 0) {
        konvaFiltersList.push(Konva.Filters.Brightness);
        result.brightness = (filters.exposure + 100) / 100;
      }
      
      if (filters.contrast !== undefined && filters.contrast !== 0) {
        konvaFiltersList.push(Konva.Filters.Contrast);
        result.contrast = filters.contrast;
      }
      
      const needsHSL = (filters.saturation !== undefined && filters.saturation !== 0) ||
                       (filters.hue !== undefined && filters.hue !== 0);
      if (needsHSL) {
        konvaFiltersList.push(Konva.Filters.HSL);
        if (filters.saturation !== undefined) result.saturation = filters.saturation / 50;
        if (filters.hue !== undefined) result.hue = filters.hue * 1.8;
      }

      if (filters.temperature !== undefined && filters.temperature !== 0) {
        konvaFiltersList.push(Konva.Filters.Temperature);
        result.temperature = filters.temperature;
      }

      if (filters.shadows !== undefined && filters.shadows !== 0) {
        konvaFiltersList.push(Konva.Filters.Shadows);
        result.shadowsAdjust = filters.shadows;
      }
      
      result.filters = konvaFiltersList.length > 0 ? konvaFiltersList : null;
      return result;
    },
    
    cacheFilteredImages() {
      const layer = this.$refs.layer;
      if (!layer) return;
      
      const stage = layer.getNode();
      if (!stage) return;
      
      this.imageNodes.forEach(node => {
        if (node.needsCache) {
          const konvaNode = stage.findOne(`#${node.config.id}`);
          if (konvaNode?.getClassName() === 'Image') {
            konvaNode.clearCache();
            konvaNode.cache();
          }
        }
      });
    },
    
    loadTexts() {
      const nodes = [];
      const scale = this.stageWidth / this.canvasWidth;
      
      for (const item of this.elements) {
        if (item.type !== 'text') continue;
        if (this.excludedItems.includes(item.id)) continue;
        if (item.groupId) continue;
        
        const scaledWidth = (item.width || 280) * scale;
        const scaledHeight = (item.height || 65) * scale;
        const scaledFontSize = (item.fontSize || 32) * scale;
        const centerX = scaledWidth / 2;
        const centerY = scaledHeight / 2;
        
        const hasShadow = item.textShadow === true;
        
        nodes.push({
          id: item.id,
          originalItem: item,
          config: {
            id: String(item.id),
            name: 'text-element',
            x: ((item.x || 50) * scale) + centerX,
            y: ((item.y || 50) * scale) + centerY,
            width: scaledWidth,
            height: scaledHeight,
            text: item.text || this.$t('legacy.Введите текст'),
            fontSize: scaledFontSize,
            fontFamily: item.fontName || 'Arial',
            fill: item.color || '#000000',
            align: 'center',
            verticalAlign: 'middle',
            rotation: item.rotate || 0,
            opacity: item.opacity !== undefined ? item.opacity : 1,
            draggable: !item.locked,
            offsetX: centerX,
            offsetY: centerY,
            wrap: 'word',
            ellipsis: false,
            shadowColor: hasShadow ? (item.shadowColor || 'rgba(0, 0, 0, 0.5)') : null,
            shadowBlur: hasShadow ? (item.shadowBlur !== undefined ? item.shadowBlur * scale : 4 * scale) : 0,
            shadowOffsetX: hasShadow ? (item.shadowOffsetX !== undefined ? item.shadowOffsetX * scale : 2 * scale) : 0,
            shadowOffsetY: hasShadow ? (item.shadowOffsetY !== undefined ? item.shadowOffsetY * scale : 2 * scale) : 0,
            shadowEnabled: hasShadow
          }
        });
      }
      
      this.textNodes = nodes.sort((a, b) => 
        (a.originalItem.zIndex || 0) - (b.originalItem.zIndex || 0)
      );
    },
    
    handleTextTransformEnd(e) {
      if (this.shouldBlockInteraction()) return;
      const node = e.target;
      const id = parseFloat(node.id());
      const scale = this.stageWidth / this.canvasWidth;
      
      const textNode = this.textNodes.find(n => n.id === id);
      if (!textNode) return;
      
      const centerX = node.width() * node.scaleX() / 2;
      const centerY = node.height() * node.scaleY() / 2;
      
      const updatedElements = this.elements.map(el => {
        if (el.id === id) {
          return {
            ...el,
            x: (node.x() - centerX) / scale,
            y: (node.y() - centerY) / scale,
            width: node.width() * node.scaleX() / scale,
            height: node.height() * node.scaleY() / scale,
            rotate: node.rotation()
          };
        }
        return el;
      });
      
      node.scaleX(1);
      node.scaleY(1);
      
      this.$emit('update:elements', updatedElements);
    },
    
    handleTextDragEnd(e) {
      if (this.shouldBlockInteraction()) return;
      const node = e.target;
      const id = parseFloat(node.id());
      const scale = this.stageWidth / this.canvasWidth;
      
      const textNode = this.textNodes.find(n => n.id === id);
      if (!textNode) return;
      
      const centerX = node.width() / 2;
      const centerY = node.height() / 2;
      
      const updatedElements = this.elements.map(el => {
        if (el.id === id) {
          return {
            ...el,
            x: (node.x() - centerX) / scale,
            y: (node.y() - centerY) / scale
          };
        }
        return el;
      });
      
      this.$emit('update:elements', updatedElements);
    },
    
    handleTextTap(e) {
      if (this.shouldBlockTap()) return;
      e.cancelBubble = true;
      const id = e.target.id();
      
      if (this.selectionMode) {
        this.toggleItemSelection(id);
        return;
      }
      
      const textNode = this.textNodes.find(n => String(n.id) === id);
      
      if (textNode) {
        this.selectedGroupId = null;
        this.selectedId = textNode.id;
        this.$emit('element-selected', textNode.id);
        this.updateTransformer();
      }
    },
    
    handleTextDblClick(e) {
      if (this.shouldBlockTap()) return;
      const target = e.target;
      const id = parseFloat(target.id());
      
      const textNode = this.textNodes.find(n => n.id === id);
      if (!textNode) return;
      
      this.editingTextId = id;
      this.editingText = textNode.originalItem.text || '';
      this.showTextEditModal = true;
      
      this.$nextTick(() => {
        if (this.$refs.textEditInput) {
          this.$refs.textEditInput.focus();
          this.$refs.textEditInput.select();
        }
      });
    },
    
    saveTextEdit() {
      if (this.editingTextId === null) return;
      
      const newText = this.editingText.trim() || this.$t('legacy.Введите текст');
      
      const updatedElements = this.elements.map(el => {
        if (el.id === this.editingTextId) {
          return { ...el, text: newText };
        }
        return el;
      });
      
      this.$emit('update:elements', updatedElements);
      this.$emit('save-history');
      this.closeTextEditModal();
    },
    
    cancelTextEdit() {
      this.closeTextEditModal();
    },
    
    closeTextEditModal() {
      this.showTextEditModal = false;
      this.editingTextId = null;
      this.editingText = '';
    },
    
    async loadGroups() {
      if (!this.groups || this.groups.length === 0) {
        this.groupNodes = [];
        return;
      }
      
      const scale = this.stageWidth / this.canvasWidth;
      const groupNodesResult = [];
      
      for (const group of this.groups) {
        const groupElements = this.elements.filter(el => el.groupId === group.id);
        if (groupElements.length === 0) continue;
        
        // BACKWARD COMPATIBILITY: поддержка старого и нового формата
        let groupX, groupY, groupWidth, groupHeight;
        const deltaX = group.deltaX || 0;
        const deltaY = group.deltaY || 0;
        
        if (group.x !== undefined && group.y !== undefined) {
          // Новый Konva-подход: группа имеет x, y, width, height
          groupX = group.x;
          groupY = group.y;
          groupWidth = group.width || 100;
          groupHeight = group.height || 100;
        } else {
          // Старый подход: вычисляем bounding box из элементов
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const item of groupElements) {
            if (item.isBackground) continue;
            const itemX = item.x + deltaX;
            const itemY = item.y + deltaY;
            minX = Math.min(minX, itemX);
            minY = Math.min(minY, itemY);
            maxX = Math.max(maxX, itemX + (item.width || 100));
            maxY = Math.max(maxY, itemY + (item.height || 100));
          }
          groupX = minX;
          groupY = minY;
          groupWidth = maxX - minX;
          groupHeight = maxY - minY;
          
          // Если есть legacy centerX/centerY, корректируем позицию группы для соответствия
          // DOM-рендерингу (чтобы центр вращения совпадал)
          if (group.centerX !== undefined && group.centerY !== undefined) {
            const legacyCenterX = group.centerX + deltaX;
            const legacyCenterY = group.centerY + deltaY;
            // Используем legacy центр для вычисления позиции группы
            groupX = legacyCenterX - groupWidth / 2;
            groupY = legacyCenterY - groupHeight / 2;
          }
        }
        
        // Центр группы - используем сохранённый если есть, иначе вычисляем
        const groupCenterX = group.centerX !== undefined 
          ? group.centerX 
          : groupX + groupWidth / 2;
        const groupCenterY = group.centerY !== undefined 
          ? group.centerY 
          : groupY + groupHeight / 2;
        
        // Масштабированные размеры
        const scaledGroupWidth = groupWidth * scale;
        const scaledGroupHeight = groupHeight * scale;
        
        // Проверяем что группа в пределах холста. Если нет - перемещаем к центру холста
        // Реальная высота холста вычисляется из ширины и аспект-рацио (4:3 → height = width * 3/4)
        const actualCanvasHeight = this.canvasWidth / (this.aspectRatio || (4/3));
        let finalGroupCenterX = groupCenterX;
        let finalGroupCenterY = groupCenterY;
        const isOffCanvas = (
          groupCenterX < -groupWidth / 2 || groupCenterX > this.canvasWidth + groupWidth / 2 ||
          groupCenterY < -groupHeight / 2 || groupCenterY > actualCanvasHeight + groupHeight / 2
        );
        if (isOffCanvas) {
          finalGroupCenterX = this.canvasWidth / 2;
          finalGroupCenterY = actualCanvasHeight / 2;
          const newGroupX = finalGroupCenterX - groupWidth / 2;
          const newGroupY = finalGroupCenterY - groupHeight / 2;
          // Обновляем позицию группы в store (только один раз — при обнаружении off-screen)
          if (!this.correctedGroupIds.has('pos-' + group.id)) {
            this.correctedGroupIds.add('pos-' + group.id);
            this.$nextTick(() => {
              const updatedGroups = this.groups.map(g => {
                if (g.id === group.id) {
                  return { ...g, x: newGroupX, y: newGroupY, centerX: finalGroupCenterX, centerY: finalGroupCenterY };
                }
                return g;
              });
              this.$emit('update:groups', updatedGroups);
            });
          }
        }
        
        console.log('🔷 Konva loadGroups:', {
          groupId: group.id,
          groupX, groupY, groupWidth, groupHeight,
          groupCenterX: finalGroupCenterX, groupCenterY: finalGroupCenterY,
          isOffCanvas,
          scale
        });
        
        const imageChildren = [];
        const textChildren = [];
        
        // Сортируем элементы группы по zIndex для корректного порядка слоёв (меньший zIndex = ниже)
        const sortedGroupElements = [...groupElements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
        
        // Новый Konva-формат: group.centerX определён, relativeX = elementCenterX - groupX (центр-based)
        // Старый формат: group.centerX не определён, relativeX = elementTopLeft - groupX (top-left based, возможно corrupted)
        const isNewKonvaFormat = group.centerX !== undefined;
        
        // Для старого формата — запускаем реконструкцию если данные повреждены
        let reconstructedXMap = {};
        let reconstructedYMap = {};
        if (!isNewKonvaFormat) {
          const elementsWithStoredRel = groupElements.filter(
            el => !el.isBackground && el.relativeX !== undefined && el.relativeX !== null
          );
          const elementsWithoutStoredRel = groupElements.filter(
            el => !el.isBackground && (el.relativeX === undefined || el.relativeX === null)
          );
          if (elementsWithStoredRel.length > 0 && elementsWithoutStoredRel.length === 0) {
            const origXValues = elementsWithStoredRel.map(el => el.relativeX + groupX);
            const origYValues = elementsWithStoredRel.map(el => el.relativeY + groupY);
            const minOrigX = Math.min(...origXValues);
            const minOrigY = Math.min(...origYValues);
            for (let i = 0; i < elementsWithStoredRel.length; i++) {
              const el = elementsWithStoredRel[i];
              reconstructedXMap[el.id] = origXValues[i] - minOrigX;
              reconstructedYMap[el.id] = origYValues[i] - minOrigY;
            }
            if (!this.correctedGroupIds.has(group.id)) {
              this.correctedGroupIds.add(group.id);
              this.$nextTick(() => {
                const correctedElements = this.elements.map(el => {
                  if (reconstructedXMap[el.id] !== undefined) {
                    return {
                      ...el,
                      x: groupX + reconstructedXMap[el.id],
                      y: groupY + reconstructedYMap[el.id],
                      relativeX: undefined,
                      relativeY: undefined
                    };
                  }
                  return el;
                });
                this.$emit('update:elements', correctedElements);
              });
            }
          }
        }
        
        for (const item of sortedGroupElements) {
          if (item.isBackground) continue;
          
          // Вычисляем позицию ЦЕНТРА элемента относительно левого верхнего угла группы
          let relativeX, relativeY;
          const itemWidth = item.width || 100;
          const itemHeight = item.height || 100;
          if (reconstructedXMap[item.id] !== undefined) {
            // Старый формат после реконструкции: top-left based → конвертируем в center-based
            relativeX = reconstructedXMap[item.id] + itemWidth / 2;
            relativeY = reconstructedYMap[item.id] + itemHeight / 2;
          } else if (item.relativeX !== undefined && item.relativeX !== null &&
              item.relativeY !== undefined && item.relativeY !== null) {
            if (isNewKonvaFormat) {
              // Новый Konva-формат: relativeX уже является center-based (= elementCenterX - groupX)
              relativeX = item.relativeX;
              relativeY = item.relativeY;
            } else {
              // Старый формат: relativeX = elementTopLeft - groupX → конвертируем в center-based
              relativeX = item.relativeX + itemWidth / 2;
              relativeY = item.relativeY + itemHeight / 2;
            }
          } else {
            // Вычисляем ЦЕНТР элемента из абсолютных координат
            const deltaX = group.deltaX || 0;
            const deltaY = group.deltaY || 0;
            const itemTopLeftX = item.x + deltaX;
            const itemTopLeftY = item.y + deltaY;
            relativeX = itemTopLeftX - groupX + itemWidth / 2;
            relativeY = itemTopLeftY - groupY + itemHeight / 2;
          }
          
          // relativeX/Y теперь хранят позицию ЦЕНТРА элемента относительно левого верхнего угла группы
          // Позиция центра элемента относительно ЦЕНТРА группы
          const relFromCenterX = relativeX - groupWidth / 2;
          const relFromCenterY = relativeY - groupHeight / 2;
          
          // Масштабируем для Konva (позиция центра элемента)
          const relX = relFromCenterX * scale;
          const relY = relFromCenterY * scale;
          
          console.log('🔷 Konva element (center-based):', {
            itemId: item.id,
            relativeX, relativeY,
            relFromCenterX, relFromCenterY,
            scaledRelX: relX,
            scaledRelY: relY,
            itemRotation: item.rotate || 0
          });
          
          if (item.type === 'text' || item.isText) {
            const scaledWidth = (item.width || 280) * scale;
            const scaledHeight = (item.height || 65) * scale;
            const scaledFontSize = (item.fontSize || 32) * scale;
            const hasShadow = item.textShadow === true;
            
            // relX/relY указывают на ЦЕНТР текста, используем offset для центрирования
            textChildren.push({
              id: item.id,
              originalItem: item,
              config: {
                id: String(item.id),
                name: 'group-text',
                x: relX,
                y: relY,
                width: scaledWidth,
                height: scaledHeight,
                offsetX: scaledWidth / 2,
                offsetY: scaledHeight / 2,
                text: item.text || this.$t('legacy.Текст'),
                fontSize: scaledFontSize,
                fontFamily: item.fontName || 'Arial',
                fill: item.color || '#000000',
                align: 'center',
                verticalAlign: 'middle',
                rotation: item.rotate || 0,
                opacity: item.opacity !== undefined ? item.opacity : 1,
                draggable: false,
                shadowColor: hasShadow ? 'rgba(0,0,0,0.5)' : undefined,
                shadowBlur: hasShadow ? 4 : 0,
                shadowOffsetX: hasShadow ? 2 : 0,
                shadowOffsetY: hasShadow ? 2 : 0
              }
            });
          } else if (item.src) {
            const imageSrc = item.src.startsWith('http') ? item.src : `${window.location.origin}${item.src}`;
            
            let img = this.groupImageCache[item.src];
            if (!img) {
              img = new Image();
              img.crossOrigin = 'anonymous';
              
              try {
                await new Promise((resolve, reject) => {
                  img.onload = resolve;
                  img.onerror = reject;
                  img.src = imageSrc;
                });
                this.groupImageCache[item.src] = img;
              } catch (e) {
                console.warn('Failed to load group element image:', item.src);
                continue;
              }
            }
            
            const scaledWidth = (item.width || 100) * scale;
            const scaledHeight = (item.height || 100) * scale;
            
            const hasFilters = item.filters && Object.keys(item.filters).some(k => item.filters[k] !== 0);
            const konvaFilters = hasFilters ? this.getKonvaFilters(item.filters) : null;
            
            const childImageSource = item.flipX
              ? this.createFlippedCanvas(img)
              : img;

            // relX/relY теперь указывают на ЦЕНТР элемента относительно центра группы
            // Используем offset для центрирования, чтобы поворот был вокруг центра
            const childConfig = {
              id: String(item.id),
              name: 'group-element',
              x: relX,
              y: relY,
              width: scaledWidth,
              height: scaledHeight,
              offsetX: scaledWidth / 2,
              offsetY: scaledHeight / 2,
              rotation: item.rotate || 0,
              scaleX: 1,
              scaleY: item.flipY ? -1 : 1,
              opacity: item.opacity !== undefined ? item.opacity : 1,
              image: childImageSource,
              draggable: false
            };
            
            if (konvaFilters && konvaFilters.filters) {
              childConfig.filters = konvaFilters.filters;
              if (konvaFilters.brightness !== undefined) childConfig.brightness = konvaFilters.brightness;
              if (konvaFilters.contrast !== undefined) childConfig.contrast = konvaFilters.contrast;
              if (konvaFilters.saturation !== undefined) childConfig.saturation = konvaFilters.saturation;
              if (konvaFilters.hue !== undefined) childConfig.hue = konvaFilters.hue;
              if (konvaFilters.temperature !== undefined) childConfig.temperature = konvaFilters.temperature;
              if (konvaFilters.shadowsAdjust !== undefined) childConfig.shadowsAdjust = konvaFilters.shadowsAdjust;
            }
            
            imageChildren.push({
              id: item.id,
              originalItem: item,
              config: childConfig,
              needsCache: hasFilters
            });
          }
        }
        
        groupNodesResult.push({
          id: group.id,
          originalGroup: group,
          imageChildren: imageChildren,
          textChildren: textChildren,
          groupCenterX: finalGroupCenterX,
          groupCenterY: finalGroupCenterY,
          config: {
            id: String(group.id),
            name: 'group',
            // Позиционируем по центру группы - дочерние элементы смещаются от (0,0) = центра
            x: finalGroupCenterX * scale,
            y: finalGroupCenterY * scale,
            width: scaledGroupWidth,
            height: scaledGroupHeight,
            // offset не нужен - группа уже позиционирована по центру
            rotation: group.rotate || 0,
            scaleX: group.flipX ? -1 : 1,
            scaleY: group.flipY ? -1 : 1,
            opacity: group.opacity !== undefined ? group.opacity : 1,
            draggable: !group.locked
          }
        });
      }
      
      this.groupNodes = groupNodesResult.sort((a, b) => 
        (a.originalGroup.zIndex || 0) - (b.originalGroup.zIndex || 0)
      );
      
      this.$nextTick(() => {
        this.cacheGroupFilteredImages();
      });
    },
    
    cacheGroupFilteredImages() {
      const layer = this.$refs.layer;
      if (!layer) return;
      
      const stage = layer.getNode();
      if (!stage) return;
      
      this.groupNodes.forEach(group => {
        group.imageChildren.forEach(child => {
          if (child.needsCache) {
            const konvaNode = stage.findOne(`#${child.config.id}`);
            if (konvaNode?.getClassName() === 'Image') {
              konvaNode.clearCache();
              konvaNode.cache();
            }
          }
        });
      });
    },
    
    loadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    },
    
    handleStageMouseDown(e) {
      if (this.shouldBlockInteraction()) return;
      this.$refs.editorContainer?.focus({ preventScroll: true });
      const stage = this.$refs.stage?.getNode();
      if (!stage) return;
      
      if (this.selectionMode) {
        const pointer = stage.getPointerPosition();
        const pos = pointer && screenToCanvas(pointer, this.viewport);
        if (pos) {
          this.isDrawingLasso = true;
          this.lassoPoints = [pos.x, pos.y];
          this.selectedItems = [];
        }
        return;
      }
      
      const isStage = e.target === e.target.getStage();
      
      if (isStage) {
        this.deselectAll();
        return;
      }
      
      const clickedOnTransformer = e.target.getParent()?.className === 'Transformer';
      if (clickedOnTransformer) {
        return;
      }
    },
    
    handleStageMouseMove() {
      if (this.shouldBlockInteraction()) return;
      if (!this.selectionMode || !this.isDrawingLasso) return;
      
      const stage = this.$refs.stage?.getNode();
      if (!stage) return;
      
      const pointer = stage.getPointerPosition();
      const pos = pointer && screenToCanvas(pointer, this.viewport);
      if (pos) {
        this.lassoPoints = [...this.lassoPoints, pos.x, pos.y];
      }
    },
    
    handleStageMouseUp() {
      if (this.shouldBlockInteraction()) return;
      if (!this.selectionMode || !this.isDrawingLasso) return;
      
      this.isDrawingLasso = false;
      
      if (this.lassoPoints.length < 6) {
        this.lassoPoints = [];
        return;
      }
      
      const polygon = [];
      for (let i = 0; i < this.lassoPoints.length; i += 2) {
        polygon.push({ x: this.lassoPoints[i], y: this.lassoPoints[i + 1] });
      }
      
      const selected = [];
      const scale = this.stageWidth / this.canvasWidth;
      
      for (const item of this.elements) {
        if (item.isBackground || item.groupId) {
          continue;
        }
        if (!item.src && item.type !== 'text') {
          continue;
        }
        
        const x = (item.x || 0) * scale;
        const y = (item.y || 0) * scale;
        const w = (item.width || 100) * scale;
        const h = (item.height || 100) * scale;
        
        const points = [
          { x: x, y: y },
          { x: x + w, y: y },
          { x: x + w, y: y + h },
          { x: x, y: y + h },
          { x: x + w / 2, y: y + h / 2 }
        ];
        
        const isInside = points.some(p => this.isPointInPolygon(p, polygon));
        
        if (isInside) {
          selected.push(item.id);
        }
      }
      
      this.selectedItems = selected;
      this.lassoPoints = [];
      
      if (selected.length > 1) {
        this.$emit('selection-complete', selected);
      }
    },
    
    isPointInPolygon(point, polygon) {
      let inside = false;
      const n = polygon.length;
      
      for (let i = 0, j = n - 1; i < n; j = i++) {
        const xi = polygon[i].x, yi = polygon[i].y;
        const xj = polygon[j].x, yj = polygon[j].y;
        
        if ((yi > point.y) !== (yj > point.y) &&
            point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi) {
          inside = !inside;
        }
      }
      
      return inside;
    },
    
    getImageConfig(item) {
      const config = { ...item.config };
      const isLocked = item.originalItem.locked;
      
      if (!this.selectionMode) {
        // Locked elements can be selected but not dragged
        config.draggable = !item.originalItem.groupId && !isLocked && !this.eraserEnabled;
      } else {
        config.draggable = false;
      }
      
      return config;
    },
    
    getSelectionRectConfig(id) {
      const item = this.elements.find(el => el.id === id);
      if (!item) return { visible: false };
      
      const scale = this.stageWidth / this.canvasWidth;
      const x = (item.x || 0) * scale;
      const y = (item.y || 0) * scale;
      const width = (item.width || 100) * scale;
      const height = (item.height || 100) * scale;
      
      return {
        x: x,
        y: y,
        width: width,
        height: height,
        stroke: '#007bff',
        strokeWidth: 2,
        dash: [5, 5],
        fill: 'rgba(0, 123, 255, 0.1)',
        listening: false
      };
    },
    
    createGroup() {
      console.log('[MobileKonvaEditor] createGroup called, selectedItems:', this.selectedItems);
      if (this.selectedItems.length < 2) {
        console.log('[MobileKonvaEditor] Less than 2 items selected');
        return;
      }
      
      const selectedElements = this.elements.filter(
        item => this.selectedItems.includes(item.id) && !item.isBackground && !item.groupId
      );
      
      console.log('[MobileKonvaEditor] selectedElements after filter:', selectedElements.length, selectedElements.map(e => ({ id: e.id, groupId: e.groupId })));
      
      if (selectedElements.length < 2) {
        console.log('[MobileKonvaEditor] Less than 2 elements after filter');
        return;
      }
      
      this.$emit('create-group', selectedElements);
      this.selectedItems = [];
    },
    
    handleImageTap(e) {
      if (this.shouldBlockTap()) return;
      const node = e.target;
      const id = node.id();
      const imageNode = this.imageNodes.find(n => String(n.id) === id);
      
      if (imageNode) {
        this.selectedGroupId = null;
        this.selectedId = imageNode.id;
        this.$emit('element-selected', imageNode.id);
        this.updateTransformer();
      }
    },
    
    handleGroupTap(e) {
      if (this.shouldBlockTap()) return;
      const node = e.target;
      let groupNode = node;
      
      while (groupNode && groupNode.name() !== 'group') {
        groupNode = groupNode.getParent();
      }
      
      if (!groupNode) return;
      
      const groupId = groupNode.id();
      const group = this.groupNodes.find(g => String(g.id) === groupId);
      if (group) {
        this.selectedId = null;
        this.selectedGroupId = group.id;
        this.$emit('group-selected', group.id);
        this.updateTransformer();
      }
    },
    
    updateTransformer() {
      const transformer = this.$refs.transformer?.getNode();
      const stage = this.$refs.stage?.getNode();
      
      if (!transformer || !stage) return;
      if (this.transformMode === 'perspective' || this.eraserEnabled) {
        transformer.nodes([]);
        return;
      }
      
      if (!this.selectedId && !this.selectedGroupId) {
        transformer.nodes([]);
        return;
      }
      
      let selectedNode = null;
      let isLocked = false;
      
      if (this.selectedId) {
        selectedNode = stage.findOne('#' + String(this.selectedId));
        const element = this.elements.find(el => el.id === this.selectedId);
        isLocked = element?.locked || false;
      } else if (this.selectedGroupId) {
        selectedNode = stage.findOne('#' + String(this.selectedGroupId));
        const group = this.groups.find(g => g.id === this.selectedGroupId);
        isLocked = group?.locked || false;
      }
      
      if (selectedNode && !isLocked) {
        transformer.nodes([selectedNode]);
      } else {
        transformer.nodes([]);
      }
    },
    
    handleTransformEnd(e) {
      if (this.shouldBlockInteraction()) return;
      const node = e.target;
      const id = node.id();
      const scale = this.stageWidth / this.canvasWidth;
      
      const newWidth = Math.abs(node.width() * node.scaleX()) / scale;
      const newHeight = Math.abs(node.height() * node.scaleY()) / scale;
      const centerX = newWidth / 2;
      const centerY = newHeight / 2;
      
      node.scaleX(1);
      node.scaleY(1);
      node.width(Math.abs(node.width()));
      node.height(Math.abs(node.height()));
      
      node.offsetX(node.width() / 2);
      node.offsetY(node.height() / 2);
      
      const updatedElements = this.elements.map(item => {
        if (String(item.id) === id) {
          return {
            ...item,
            x: (node.x() / scale) - centerX,
            y: (node.y() / scale) - centerY,
            width: newWidth,
            height: newHeight,
            rotate: node.rotation(),
            flipX: item.flipX || false
          };
        }
        return item;
      });
      
      this.$emit('update:elements', updatedElements);
    },
    
    handleDragEnd(e) {
      if (this.shouldBlockInteraction()) return;
      const node = e.target;
      const id = node.id();
      const scale = this.stageWidth / this.canvasWidth;
      
      const currentWidth = Math.abs(node.width() * node.scaleX()) / scale;
      const currentHeight = Math.abs(node.height() * node.scaleY()) / scale;
      const centerX = currentWidth / 2;
      const centerY = currentHeight / 2;
      
      const updatedElements = this.elements.map(item => {
        if (String(item.id) === id) {
          return {
            ...item,
            x: (node.x() / scale) - centerX,
            y: (node.y() / scale) - centerY
          };
        }
        return item;
      });
      
      this.$emit('update:elements', updatedElements);
    },
    
    handleGroupTransformEnd(e) {
      if (this.shouldBlockInteraction()) return;
      const node = e.target;
      const id = node.id();
      const scale = this.stageWidth / this.canvasWidth;
      
      const newScaleX = node.scaleX();
      const newScaleY = node.scaleY();
      const newWidth = Math.abs(node.width() * newScaleX) / scale;
      const newHeight = Math.abs(node.height() * newScaleY) / scale;
      const centerX = newWidth / 2;
      const centerY = newHeight / 2;
      
      const newGroupX = (node.x() / scale) - centerX;
      const newGroupY = (node.y() / scale) - centerY;
      
      const targetGroup = this.groups.find(g => String(g.id) === id);
      if (!targetGroup) return;
      
      const scaleRatioX = newWidth / (targetGroup.width || 1);
      const scaleRatioY = newHeight / (targetGroup.height || 1);
      
      // Вычисляем новый центр группы
      const newCenterX = node.x() / scale;
      const newCenterY = node.y() / scale;
      
      const updatedGroups = this.groups.map(group => {
        if (String(group.id) === id) {
          return {
            ...group,
            x: newGroupX,
            y: newGroupY,
            width: newWidth,
            height: newHeight,
            centerX: newCenterX,
            centerY: newCenterY,
            rotate: node.rotation(),
            flipX: newScaleX < 0,
            flipY: newScaleY < 0
          };
        }
        return group;
      });
      
      const updatedElements = this.elements.map(item => {
        if (item.groupId === targetGroup.id) {
          const relX = item.relativeX !== undefined ? item.relativeX : (item.x - targetGroup.x);
          const relY = item.relativeY !== undefined ? item.relativeY : (item.y - targetGroup.y);
          
          const newRelX = relX * scaleRatioX;
          const newRelY = relY * scaleRatioY;
          
          return {
            ...item,
            relativeX: newRelX,
            relativeY: newRelY,
            x: newGroupX + newRelX,
            y: newGroupY + newRelY,
            width: (item.width || 100) * scaleRatioX,
            height: (item.height || 100) * scaleRatioY
          };
        }
        return item;
      });
      
      // Сбрасываем scale на 1/-1 и обновляем width/height
      node.scaleX(newScaleX < 0 ? -1 : 1);
      node.scaleY(newScaleY < 0 ? -1 : 1);
      node.width(Math.abs(node.width() * newScaleX));
      node.height(Math.abs(node.height() * newScaleY));
      // НЕ устанавливаем offset - группа уже позиционирована по центру
      
      console.log('🔷 handleGroupTransformEnd:', {
        nodeX: node.x(),
        nodeY: node.y(),
        newCenterX,
        newCenterY,
        newGroupX,
        newGroupY,
        rotation: node.rotation()
      });
      
      this.$emit('update:elements', updatedElements);
      this.$emit('update:groups', updatedGroups);
    },
    
    handleGroupDragEnd(e) {
      if (this.shouldBlockInteraction()) return;
      const node = e.target;
      const id = node.id();
      const scale = this.stageWidth / this.canvasWidth;
      
      // Находим группу для получения размеров
      const groupData = this.groupNodes.find(g => String(g.id) === id);
      if (!groupData) return;
      
      // node.x(), node.y() - позиция центра группы в Konva
      const newCenterX = node.x() / scale;
      const newCenterY = node.y() / scale;
      
      // Используем сохранённый центр из groupData (который уже вычислен в loadGroups)
      const oldCenterX = groupData.groupCenterX;
      const oldCenterY = groupData.groupCenterY;
      
      const deltaX = newCenterX - oldCenterX;
      const deltaY = newCenterY - oldCenterY;
      
      // Обновляем x, y группы (смещаем на дельту)
      const newX = groupData.originalGroup.x + deltaX;
      const newY = groupData.originalGroup.y + deltaY;
      
      console.log('🔷 handleGroupDragEnd:', {
        nodeX: node.x(),
        nodeY: node.y(),
        newCenterX, newCenterY,
        oldCenterX, oldCenterY,
        deltaX, deltaY,
        newX, newY
      });
      
      // Обновляем x, y и centerX, centerY группы
      const updatedGroups = this.groups.map(group => {
        if (String(group.id) === id) {
          return {
            ...group,
            x: newX,
            y: newY,
            centerX: newCenterX,
            centerY: newCenterY
          };
        }
        return group;
      });
      
      // Обновляем абсолютные координаты элементов группы на ту же дельту,
      // чтобы при сохранении и загрузке element.x - group.x давал корректный relativeX
      const updatedElements = this.elements.map(el => {
        if (String(el.groupId) === id) {
          return {
            ...el,
            x: el.x + deltaX,
            y: el.y + deltaY
          };
        }
        return el;
      });
      
      this.$emit('update:elements', updatedElements);
      this.$emit('update:groups', updatedGroups);
    },
    
    deselectAll() {
      this.selectedId = null;
      this.selectedGroupId = null;
      this.$emit('element-selected', null);
      this.$emit('group-selected', null);
      this.updateTransformer();
    },
    
    deleteSelected() {
      if (this.selectedId) {
        this.$emit('element-deleted', this.selectedId);
        this.selectedId = null;
        this.updateTransformer();
      } else if (this.selectedGroupId) {
        this.$emit('group-deleted', this.selectedGroupId);
        this.selectedGroupId = null;
        this.updateTransformer();
      }
    }
  }
};
</script>

<style scoped>
.mobile-konva-editor {
  width: 100%;
  position: relative;
  touch-action: none;
  overflow: hidden;
  outline: none;
}
.mobile-konva-editor:focus-visible { outline: 2px solid #55768f; outline-offset: 2px; }
.mobile-konva-editor.pan-ready { cursor: grab; }
.mobile-konva-editor.pan-moving { cursor: grabbing; }
.viewport-background {
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: top left;
  pointer-events: none;
}
.mobile-konva-editor.erasing { cursor: none; }
.eraser-cursor {
  position: absolute;
  border: 1px solid #fff;
  box-shadow: 0 0 0 1px #26374e;
  border-radius: 50%;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 2;
}

.text-edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.text-edit-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.text-edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.text-edit-header span {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.text-edit-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.text-edit-close:hover {
  color: #1f2937;
}

.text-edit-body {
  padding: 20px;
}

.text-edit-textarea {
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;
}

.text-edit-textarea:focus {
  border-color: #4285f4;
  box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.15);
}

.text-edit-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.text-edit-btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.text-edit-btn.cancel {
  background: #e5e7eb;
  border: none;
  color: #4b5563;
}

.text-edit-btn.cancel:hover {
  background: #d1d5db;
}

.text-edit-btn.save {
  background: #4285f4;
  border: none;
  color: white;
}

.text-edit-btn.save:hover {
  background: #3367d6;
}
</style>
